import axios from 'axios';
import {
  addActingOrganizationHeader,
  exposeValidationErrors,
  freshAccessToken,
  handleOrganizationError,
  replayRateLimited,
  reportMaintenance,
} from '@koob/utils';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { API_NODE_BASE } from '../constants';
import { refreshToken, revokeToken } from './auth';

const api = axios.create({ baseURL: API_NODE_BASE });

const formatConfig = ({ params, ...opts } = {}) => ({
  ...opts,
  params: decamelizeKeys(params),
});

const isFormData = val =>
  typeof FormData !== 'undefined' && val instanceof FormData;

api.interceptors.request.use(config => {
  const impersonationToken = localStorage.getItem('impersonationToken');
  const accessToken =
    impersonationToken?.length > 0
      ? impersonationToken
      : localStorage.getItem('accessToken');

  config.headers['Accept'] = 'application/json';

  if (Boolean(accessToken)) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (!isFormData(config.data)) {
    config.data = camelizeKeys(config.data);
  }

  return addActingOrganizationHeader(config);
});

// The error body is a plain string on some routes and a { message } object on
// others; read both shapes without assuming either.
const bodyText = data =>
  typeof data === 'string' ? data : (data?.message ?? '');

api.interceptors.response.use(
  response => response,
  async error => {
    reportMaintenance(error);
    // KD-3991: a read refused by the rate limit waits and goes again.
    const replay = replayRateLimited(error, api);
    if (replay) return replay;
    // A refused X-Organization-Id is not an expired session (KD-3735).
    if (handleOrganizationError(error)) return Promise.reject(error);
    const impersonationToken = localStorage.getItem('impersonationToken');
    const impersonating = impersonationToken?.length > 0;
    // KD-3907: an expired access token is refreshed first; only a session
    // that cannot be refreshed logs out.
    if (error.response?.status === 401 && !impersonating) {
      const token = await freshAccessToken(error.config, refreshToken);
      if (token) {
        error.config.headers['Authorization'] = `Bearer ${token}`;
        return axios(error.config);
      }
    }
    if (
      (error.response?.status === 400 &&
        bodyText(error.response?.data).includes('Credentials are invalid.')) ||
      (error.response?.status === 401 &&
        (bodyText(error.response?.data).includes('Unauthorized') ||
          // Newer API builds return a JSON envelope with a stable code.
          error.response?.data?.code === 'AUTHENTICATION_REQUIRED'))
    ) {
      if (impersonating) {
        await revokeToken(true);
        localStorage.setItem('impersonationToken', '');
      } else {
        await revokeToken();
        localStorage.setItem('accessToken', '');
        localStorage.setItem('refreshToken', '');
        localStorage.setItem('impersonationToken', '');
      }
      window.location.reload();
      return;
    }

    // After the session logic, which matches on the API's original message.
    exposeValidationErrors(error);

    // A failed login or refresh: the caller handles it.
    if (error.config?.url?.includes('oauth/token')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const hadSession = Boolean(localStorage.getItem('refreshToken'));
      localStorage.setItem('accessToken', '');
      localStorage.setItem('refreshToken', '');
      localStorage.removeItem('impersonationToken');
      if (!hadSession) window.location.reload();
    }

    return Promise.reject(error);
  },
);

const formatResponse = response => {
  if (!Boolean(response)) {
    return response;
  }

  return camelizeKeys(response);
};

export const get = (uri, config = {}, formatResp = true) =>
  api
    .get(uri, formatConfig(config))
    .then(formatResp ? formatResponse : response => response);

export const post = (uri, payload = {}, config, formatResp = true) =>
  api
    .post(uri, payload, formatConfig(config))
    .then(formatResp ? formatResponse : response => response);

export const put = (uri, payload = {}, config) =>
  api.put(uri, payload, formatConfig(config)).then(formatResponse);

export const putFile = (uri, payload) =>
  api
    .put(uri, payload, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then(formatResponse);

export const patch = (uri, payload = {}, config) =>
  api.patch(uri, payload, formatConfig(config)).then(formatResponse);

export const destroy = (uri, config) =>
  api.delete(uri, formatConfig(config)).then(formatResponse);
